import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, ROLE_LABELS } from "@/lib/api";
import { useLoad, Loading, RagBadge, PhaseStepper, Pill, FormDialog } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

function ProjectCompanies({ p, reload }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const comps = useLoad(() => (open ? api.get("/companies?include_retired=true").then((r) => r.data.filter((c) => c.active || (p.company_ids || []).includes(c.id))) : Promise.resolve(null)), [open]);
  const can = ["pmo", "pgm", "sysadmin"].includes(user.role);
  return (
    <>
      <span data-testid="project-companies">· {p.company_names?.length ? p.company_names.join(", ") : "Unassigned company"}</span>
      {can && <button className="text-indigo-600 hover:underline" data-testid="change-companies-btn" onClick={() => setOpen(true)}>change</button>}
      {open && comps.data && <FormDialog open={open} onOpenChange={setOpen} title="Companies for this project" testid="project-companies-form" initial={{ company_ids: p.company_ids || [] }}
        fields={[{ name: "company_ids", label: "Companies", type: "multicheck", required: true, options: comps.data.map((c) => ({ value: c.id, label: c.active ? c.name : `${c.name} (retired)` })) }]}
        onSubmit={async (v) => { await api.put(`/projects/${p.id}/companies`, v); reload(); }} />}
    </>
  );
}
import Overview from "@/pages/project/Overview";
import Plan from "@/pages/project/Plan";
import Budget from "@/pages/project/Budget";
import Raid from "@/pages/project/Raid";
import Changes from "@/pages/project/Changes";
import Documents from "@/pages/project/Documents";
import Reports from "@/pages/project/Reports";
import Team from "@/pages/project/Team";
import Gates from "@/pages/project/Gates";
import ProjectMeetings from "@/pages/project/ProjectMeetings";
import Audit from "@/pages/project/Audit";
import Benefits from "@/pages/project/Benefits";
import Deliveries from "@/pages/project/Deliveries";

const TABS = [["overview", "Overview", Overview], ["plan", "Plan", Plan], ["budget", "Budget", Budget], ["raid", "RAID", Raid], ["changes", "Changes", Changes], ["benefits", "Benefits", Benefits],
  ["documents", "Documents", Documents], ["deliveries", "Basecamp deliveries", Deliveries], ["reports", "Reports", Reports], ["team", "Team", Team], ["gates", "Gates", Gates], ["meetings", "Meetings", ProjectMeetings], ["audit", "Audit", Audit]];

export default function ProjectWorkspace() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const { data: p, error, reload } = useLoad(() => api.get(`/projects/${id}`).then((r) => r.data), [id]);
  const { data: users } = useLoad(() => api.get("/users").then((r) => r.data));
  const [, force] = useState(0);
  if (!p) return <Loading error={error} />;
  const visible = TABS.filter(([k]) => !(k === "budget" && p.fin_visibility === "none"));
  const ctx = { p, reload: () => { reload(); force((x) => x + 1); }, users: users || [], go: (t) => setParams({ tab: t }) };
  return (
    <>
      <div className="space-y-3 fade-up">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="font-mono">{p.code}</span>·<span>{p.class}</span>{p.programme_name && <>·<span>{p.programme_name}</span></>}·<span>PM {p.pm_name}</span>
          <Pill tone="indigo" testid="my-project-role">your role: {ROLE_LABELS[p.my_role] || p.my_role}</Pill>
          <ProjectCompanies p={p} reload={ctx.reload} />
          {p.locked && <Pill tone="red" testid="project-locked"><Lock className="mr-1 h-3 w-3" />Closed · read-only</Pill>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold sm:text-3xl" data-testid="project-title">{p.name}</h1>
          <RagBadge value={p.rag?.overall} testid="project-overall-rag" />
        </div>
        <PhaseStepper phase={p.phase} />
      </div>
      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="h-auto flex-wrap justify-start">
          {visible.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`project-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        {visible.map(([k, , C]) => <TabsContent key={k} value={k} className="space-y-4">{tab === k && <C {...ctx} />}</TabsContent>)}
      </Tabs>
    </>
  );
}
